import axios from "axios";
import { DataGrid } from "devextreme-react";
import {
  Column,
  Editing,
  Form,
  Label,
  LoadPanel,
  Popup,
  Scrolling,
  SearchPanel,
  Selection,
  Texts,
} from "devextreme-react/data-grid";
import { EmptyItem, SimpleItem } from "devextreme-react/form";
import CustomStore from "devextreme/data/custom_store";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import TwoDateBoxComponent2 from "./TwoDateBoxComponent2";

export default function OtherExperienceOrHistory() {
  const [dataSource, setDataSource] = useState(null);
  const [formDataSource, setFormDataSource] = useState(null);

  const userInfo = useSelector((state) => state.userInfo);

  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const otherExperienceOrHistoryGridRef = useRef(null);

  useEffect(() => {
    setDataSource(
      new CustomStore({
        key: "SEQ",
        load: async () => {
          const result = await axios.get(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/his/otrExpHis/${userInfo.employeeNo}`
          );

          return result.data;
        },
        insert: async (values) => {
          values = {
            ...values,
            startDate: startDateRef.current.instance.option("value"),
            endDate: endDateRef.current.instance.option("value"),
          };

          await axios.post(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/his/otrExpHis/${userInfo.employeeNo}`,
            values
          );
        },
        update: async (key, values) => {
          values = {
            ...values,
            startDate: startDateRef.current.instance.option("value"),
            endDate: endDateRef.current.instance.option("value"),
          };

          await axios.put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/his/otrExpHis/${userInfo.employeeNo}/${key}`,
            values
          );
        },
        remove: async (key) => {
          await axios.delete(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/his/otrExpHis/${userInfo.employeeNo}/${key}`
          );
        },
      })
    );
  }, []);

  const onEditingStart = (e) => {
    if (e.data.startDate) {
      setFormDataSource((prevState) => ({
        ...prevState,
        startDate: e.data.startDate,
      }));
    }

    if (e.data.endDate) {
      setFormDataSource((prevState) => ({
        ...prevState,
        endDate: e.data.endDate,
      }));
    }
  };

  const onInitNewRow = (e) => {
    const key =
      otherExperienceOrHistoryGridRef.current.instance.getKeyByRowIndex(0);

    if (key) {
      e.component.selectRows(key, true);
      e.data =
        otherExperienceOrHistoryGridRef.current.instance.getSelectedRowsData()[0];

      if (e.data.startDate) {
        setFormDataSource((prevState) => ({
          ...prevState,
          startDate: e.data.startDate,
        }));
      }

      if (e.data.endDate) {
        setFormDataSource((prevState) => ({
          ...prevState,
          endDate: e.data.endDate,
        }));
      }
    }
  };

  return (
    <DataGrid
      id="otherExperienceOrHistoryHistory"
      ref={otherExperienceOrHistoryGridRef}
      dataSource={dataSource}
      keyExpr="SEQ"
      rowAlternationEnabled={true}
      showBorders={true}
      showColumnLines={true}
      allowColumnResizing={true}
      columnAutoWidth={true}
      wordWrapEnabled={true}
      allowColumnReordering={true}
      style={{
        maxHeight: "calc(100vh - 180px)",
        marginLeft: "10px",
        marginRight: "10px",
        // marginTop: "56px",}
      }}
      hoverStateEnabled={true}
      showColumnHeaders={true}
      onEditingStart={onEditingStart}
      onInitNewRow={onInitNewRow}
    >
      <LoadPanel enabled={true} />

      <SearchPanel visible={true} />

      <Scrolling mode={"virtual"} showScrollbar={"always"} />

      <Selection mode={"single"} />

      <Editing
        mode={"popup"}
        allowAdding={true}
        allowUpdating={true}
        allowDeleting={true}
      >
        <Texts
          confirmDeleteTitle={"Delete the other experience or history"}
          confirmDeleteMessage="Are you sure you want to delete this other experience or history?"
        />
        <Popup
          title={"Other Experience or History"}
          showTitle={true}
          height="auto"
        />
        <Form
          labelMode={"outside"}
          showColonAfterLabel={false}
          labelLocation="top"
          minColWidth={300}
          colCount={2}
          colCountByScreen={{ sm: 2, md: 4 }}
          screenByWidth={(width) => {
            return width < 720 ? "sm" : "md";
          }}
          validationGroup="awards"
          height={"100%"}
        >
          <SimpleItem
            dataField={"desi"}
            editorType="dxTextBox"
            editorOptions={{
              showClearButton: true,
            }}
          >
            <Label text={"명칭"} />
          </SimpleItem>
          <SimpleItem
            dataField={"actAgcy"}
            editorType="dxTextBox"
            editorOptions={{
              showClearButton: true,
            }}
          >
            <Label text={"활동기관"} />
          </SimpleItem>
          <SimpleItem>
            <Label text={"활동기간"} />
            <TwoDateBoxComponent2
              firstDateBox={{
                placeholder: "활동시작일",
                field: "startDate",
                ref: startDateRef,
              }}
              secondDateBox={{
                // placeholder: "계약종료일",
                placeholder: "Contract End Date",
                field: "endDate",
                ref: endDateRef,
              }}
              formDataSource={formDataSource}
            />
          </SimpleItem>
          <EmptyItem />
          <SimpleItem
            dataField={"details"}
            editorType="dxTextBox"
            editorOptions={{
              showClearButton: true,
            }}
            colSpan={4}
          >
            <Label text="상세내용" />
          </SimpleItem>
        </Form>
      </Editing>

      <Column dataField={"desi"} caption="명칭" />
      <Column dataField={"actAgcy"} caption="활동기관" />
      <Column
        dataField={"startDate"}
        caption="활동시작일"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
      />
      <Column
        dataField={"endDate"}
        caption="활동종료일"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
      />
      <Column dataField={"details"} caption="상세내용" />
    </DataGrid>
  );
}
